var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "../../lib/utils.js";
import { cva } from "class-variance-authority";
// Updated Card variants to match Figma design
var cardVariants = cva("rounded-xl bg-background overflow-hidden", {
    variants: {
        variant: {
            default: "bg-background",
            dark: "bg-card text-card-foreground",
        },
        shadow: {
            none: "",
            border: "shadow-[0px_0px_0px_1px_rgba(46,43,41,0.10)]", // Border-like shadow
            soft: "shadow-[0px_2px_8px_-2px_rgba(46,43,41,0.10)]", // Soft shadow
            medium: "shadow-[0px_8px_16px_-4px_rgba(46,43,41,0.10)]", // Medium shadow
            strong: "shadow-[0px_24px_40px_-8px_rgba(46,43,41,0.10)]", // Strong shadow
        },
    },
    defaultVariants: {
        variant: "default",
        shadow: "border",
    },
});
// Base Card component with Figma styling
var Card = React.forwardRef(function (_a, ref) {
    var className = _a.className, variant = _a.variant, shadow = _a.shadow, props = __rest(_a, ["className", "variant", "shadow"]);
    return (_jsx("div", __assign({ ref: ref, className: cn(cardVariants({ variant: variant, shadow: shadow }), className) }, props)));
});
Card.displayName = "Card";
// 2. Update the CardHeader component to use ChevronDown
var CardHeader = React.forwardRef(function (_a, ref) {
    var className = _a.className, _b = _a.variant, variant = _b === void 0 ? 'default' : _b, _c = _a.showIcon, showIcon = _c === void 0 ? true : _c, _d = _a.showMoreIcon, showMoreIcon = _d === void 0 ? true : _d, icon = _a.icon, children = _a.children, props = __rest(_a, ["className", "variant", "showIcon", "showMoreIcon", "icon", "children"]);
    return (_jsxs("div", __assign({ ref: ref, className: cn("flex justify-between items-center p-4", variant === 'dark' ? "bg-card" : "bg-background", "rounded-t-xl", className) }, props, { children: [_jsxs("div", { className: "flex-1 flex justify-start items-center gap-2", children: [showIcon && (icon || _jsx(Diamond, {})), _jsx("div", { className: cn("text-base font-semibold leading-[19.2px]", variant === 'dark' ? "text-card-foreground" : "text-foreground"), children: children })] }), showMoreIcon && (_jsx(ChevronDown, { className: cn("w-[18px] h-[18px]", variant === 'dark' ? "text-[#A09D9B] dark:text-muted-foreground" : "text-[#585755] dark:text-muted-foreground") }))] })));
});
CardHeader.displayName = "CardHeader";
// Card Title component
var CardTitle = React.forwardRef(function (_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("h3", __assign({ ref: ref, className: cn("text-base font-semibold leading-[19.2px] tracking-tight", className) }, props)));
});
CardTitle.displayName = "CardTitle";
var CardDescription = React.forwardRef(function (_a, ref) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (_jsx("p", __assign({ ref: ref, className: cn("text-sm text-muted-foreground", className) }, props)));
});
CardDescription.displayName = "CardDescription";
// Card Content with Figma styling
var CardContent = React.forwardRef(function (_a, ref) {
    var className = _a.className, _b = _a.variant, variant = _b === void 0 ? 'default' : _b, _c = _a.showPlaceholder, showPlaceholder = _c === void 0 ? false : _c, children = _a.children, props = __rest(_a, ["className", "variant", "showPlaceholder", "children"]);
    return (_jsx("div", __assign({ ref: ref, className: cn("p-4 flex flex-col justify-start items-start gap-2", className) }, props, { children: showPlaceholder ? (_jsx("div", { className: "w-full h-[110px] relative", children: _jsx("div", { className: "w-full h-[110px] bg-muted rounded-lg border border-input" }) })) : children })));
});
CardContent.displayName = "CardContent";
// Button variants to match Figma
var buttonVariants = cva("px-[10px] py-2 rounded-lg flex justify-center items-center gap-1 text-sm font-semibold leading-[18.2px] transition-colors", {
    variants: {
        variant: {
            primary: "bg-[#DAF565] dark:bg-primary text-[#242424] dark:text-foreground hover:bg-[#D1F055] dark:hover:bg-primary-hover",
            secondary: "bg-[#E5E1DF] dark:bg-secondary text-[#242424] dark:text-foreground hover:bg-[#DDD9D7] dark:hover:bg-secondary-hover",
            tertiary: "bg-white dark:bg-background text-[#242424] dark:text-foreground hover:bg-[#F5F5F5] dark:hover:bg-muted",
            tertiaryDark: "bg-[#242424] dark:bg-card text-[#FAF4F0] dark:text-card-foreground hover:bg-[#333333] dark:hover:bg-accent",
        },
    },
    defaultVariants: {
        variant: "secondary",
    },
});
var Button = React.forwardRef(function (_a, ref) {
    var className = _a.className, variant = _a.variant, props = __rest(_a, ["className", "variant"]);
    return (_jsx("button", __assign({ ref: ref, className: cn(buttonVariants({ variant: variant }), className) }, props)));
});
Button.displayName = "Button";
// Card Footer with buttons (matching Figma)
var CardFooter = React.forwardRef(function (_a, ref) {
    var className = _a.className, _b = _a.variant, variant = _b === void 0 ? 'default' : _b, _c = _a.buttons, buttons = _c === void 0 ? [] : _c, children = _a.children, props = __rest(_a, ["className", "variant", "buttons", "children"]);
    return (_jsx("div", __assign({ ref: ref, className: cn("flex justify-end items-center gap-2 p-4", variant === 'dark' ? "bg-card" : "bg-background", "rounded-b-xl", className) }, props, { children: buttons.length > 0 ? (buttons.map(function (button, index) { return (_jsx(Button, { variant: variant === 'dark' && button.variant === 'tertiary'
                ? 'tertiaryDark'
                : button.variant, onClick: button.onClick, children: button.label }, index)); })) : children })));
});
CardFooter.displayName = "CardFooter";
export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, Button, cardVariants, buttonVariants };
// Enhanced DynamicCard with updated Badge integration
import { TrendingUp, TrendingDown, Minus, ChevronDown, Diamond } from 'lucide-react';
import { Badge } from './badge.js';
import { Progress } from './progress.js';
// Utility functions
var formatValue = function (value, type, prefix, suffix, formatter) {
    if (formatter)
        return formatter(value);
    var stringValue = value.toString();
    var prefixStr = prefix || '';
    var suffixStr = suffix || '';
    switch (type) {
        case 'currency':
            var numValue = typeof value === 'number' ? value : parseFloat(stringValue);
            return "".concat(prefixStr, "$").concat(numValue.toLocaleString()).concat(suffixStr);
        case 'number':
            var num = typeof value === 'number' ? value : parseFloat(stringValue);
            return "".concat(prefixStr).concat(num.toLocaleString()).concat(suffixStr);
        case 'percentage':
            return "".concat(prefixStr).concat(stringValue, "%").concat(suffixStr);
        case 'text':
            return "".concat(prefixStr).concat(stringValue).concat(suffixStr);
        case 'custom':
            return stringValue;
        default:
            return "".concat(prefixStr).concat(stringValue).concat(suffixStr);
    }
};
var getTrendIcon = function (direction, showIcon) {
    if (showIcon === void 0) { showIcon = true; }
    if (!showIcon)
        return null;
    switch (direction) {
        case 'up':
            return _jsx(TrendingUp, { className: "h-4 w-4" });
        case 'down':
            return _jsx(TrendingDown, { className: "h-4 w-4" });
        case 'neutral':
            return _jsx(Minus, { className: "h-4 w-4" });
        default:
            return null;
    }
};
var getTrendColor = function (direction, colorScheme) {
    if (colorScheme) {
        switch (colorScheme) {
            case 'success':
                return 'text-success';
            case 'warning':
                return 'text-yellow-600';
            case 'destructive':
                return 'text-destructive';
            default:
                return 'text-muted-foreground';
        }
    }
    switch (direction) {
        case 'up':
            return 'text-success';
        case 'down':
            return 'text-destructive';
        case 'neutral':
            return 'text-muted-foreground';
        default:
            return 'text-muted-foreground';
    }
};
// Map size to padding classes
var sizeClasses = {
    sm: 'text-sm',
    md: '',
    lg: 'text-lg',
    xl: 'text-xl',
};
// Enhanced DynamicCard component
export var DynamicCard = function (_a) {
    var _b = _a.variant, variant = _b === void 0 ? 'default' : _b, _c = _a.shadow, shadow = _c === void 0 ? 'border' : _c, _d = _a.size, size = _d === void 0 ? 'md' : _d, _e = _a.layout, layout = _e === void 0 ? 'vertical' : _e, _f = _a.sections, sections = _f === void 0 ? [] : _f, title = _a.title, subtitle = _a.subtitle, description = _a.description, value = _a.value, _g = _a.valueType, valueType = _g === void 0 ? 'text' : _g, valuePrefix = _a.valuePrefix, valueSuffix = _a.valueSuffix, valueFormatter = _a.valueFormatter, trend = _a.trend, _h = _a.className, className = _h === void 0 ? '' : _h, _j = _a.actions, actions = _j === void 0 ? [] : _j, _k = _a.badges, badges = _k === void 0 ? [] : _k, _l = _a.clickable, clickable = _l === void 0 ? false : _l, onClick = _a.onClick, progress = _a.progress, customContent = _a.customContent, _m = _a.loading, loading = _m === void 0 ? false : _m, _o = _a.disabled, disabled = _o === void 0 ? false : _o, _p = _a.selected, selected = _p === void 0 ? false : _p, _q = _a.showIcon, showIcon = _q === void 0 ? true : _q, _r = _a.showMoreIcon, showMoreIcon = _r === void 0 ? true : _r, headerIcon = _a.headerIcon, _s = _a.showPlaceholder, showPlaceholder = _s === void 0 ? false : _s;
    // Compose root Card classes
    var cardClasses = cn(clickable && 'cursor-pointer hover:shadow-soft transition-shadow', selected && 'ring-2 ring-primary', disabled && 'opacity-50 pointer-events-none', className);
    var handleCardClick = function () {
        if (clickable && onClick && !disabled) {
            onClick();
        }
    };
    var renderValue = function () {
        if (!value && value !== 0)
            return null;
        var formattedValue = formatValue(value, valueType, valuePrefix, valueSuffix, valueFormatter);
        return (_jsx("div", { className: "text-2xl font-bold", children: formattedValue }));
    };
    var renderTrend = function () {
        if (!trend)
            return null;
        var trendColor = getTrendColor(trend.direction, trend.colorScheme);
        var trendIcon = getTrendIcon(trend.direction, trend.showIcon);
        return (_jsxs("div", { className: "flex items-center space-x-1 text-sm ".concat(trendColor), children: [trendIcon, _jsx("span", { className: "font-medium", children: typeof trend.value === 'number' ? trend.value.toLocaleString() : trend.value }), trend.label && _jsx("span", { className: "text-muted-foreground", children: trend.label })] }));
    };
    var renderBadges = function () {
        if (badges.length === 0)
            return null;
        return (_jsx("div", { className: "flex flex-wrap gap-1", children: badges.map(function (badge, index) { return (_jsx(Badge, { variant: badge.variant || 'default', className: badge.className, children: badge.text }, index)); }) }));
    };
    var renderProgress = function () {
        if (!progress)
            return null;
        return (_jsxs("div", { className: "space-y-2", children: [progress.label && (_jsxs("div", { className: "flex items-center justify-between text-sm", children: [_jsx("span", { children: progress.label }), progress.showPercentage && (_jsxs("span", { className: "text-muted-foreground", children: [Math.round((progress.value / (progress.max || 100)) * 100), "%"] }))] })), _jsx(Progress, { value: progress.value, max: progress.max || 100 })] }));
    };
    if (loading) {
        return (_jsx(Card, { variant: variant, shadow: shadow, className: cardClasses, children: _jsx(CardContent, { className: "flex items-center justify-center h-32", children: _jsxs("div", { className: "animate-pulse space-y-2 w-full", children: [_jsx("div", { className: "h-4 bg-muted rounded w-3/4" }), _jsx("div", { className: "h-6 bg-muted rounded w-1/2" }), _jsx("div", { className: "h-3 bg-muted rounded w-2/3" })] }) }) }));
    }
    return (_jsx(Card, { variant: variant, shadow: shadow, className: cardClasses, onClick: handleCardClick, children: sections && sections.length > 0 ? (sections.map(function (section, index) {
            var type = section.type, content = section.content, secClassName = section.className;
            switch (type) {
                case 'header':
                    return (_jsx(CardHeader, { className: secClassName, variant: variant, showIcon: showIcon, showMoreIcon: showMoreIcon, icon: headerIcon, children: content }, index));
                case 'content':
                    return (_jsx(CardContent, { className: secClassName, variant: variant, children: content }, index));
                case 'footer':
                    return (_jsx(CardFooter, { className: secClassName, variant: variant, children: content }, index));
                default:
                    return _jsx(React.Fragment, { children: content }, index);
            }
        })) : (_jsxs(_Fragment, { children: [title && (_jsx(CardHeader, { variant: variant, showIcon: showIcon, showMoreIcon: showMoreIcon, icon: headerIcon, children: title })), _jsx(CardContent, { variant: variant, showPlaceholder: showPlaceholder, children: !showPlaceholder && (_jsxs(_Fragment, { children: [renderValue(), renderTrend(), renderProgress(), renderBadges(), customContent, description && (_jsx("p", { className: "text-sm text-muted-foreground pt-2", children: description }))] })) }), actions.length > 0 && (_jsx(CardFooter, { variant: variant, buttons: actions.map(function (action) { return ({
                        label: action.label,
                        variant: action.variant,
                        onClick: action.onClick,
                    }); }) }))] })) }));
};
// Pre-built card variants with updated styling
export var KPICard = function (_a) {
    var title = _a.title, value = _a.value, trend = _a.trend, _b = _a.shadow, shadow = _b === void 0 ? 'border' : _b, _c = _a.variant, variant = _c === void 0 ? 'default' : _c, className = _a.className;
    return (_jsx(DynamicCard, { title: title, value: value, valueType: "text", trend: trend, shadow: shadow, variant: variant, className: className, size: 'sm' }));
};
// Pre-built metric card
export var MetricCard = function (_a) {
    var title = _a.title, value = _a.value, _b = _a.valueType, valueType = _b === void 0 ? 'number' : _b, trend = _a.trend, progress = _a.progress, _c = _a.shadow, shadow = _c === void 0 ? 'border' : _c, _d = _a.variant, variant = _d === void 0 ? 'default' : _d, className = _a.className;
    return (_jsx(DynamicCard, { title: title, value: value, valueType: valueType, trend: trend, progress: progress, shadow: shadow, variant: variant, className: className }));
};
// Pre-built action card
export var ActionCard = function (_a) {
    var title = _a.title, description = _a.description, actions = _a.actions, badges = _a.badges, _b = _a.shadow, shadow = _b === void 0 ? 'border' : _b, _c = _a.variant, variant = _c === void 0 ? 'default' : _c, className = _a.className;
    return (_jsx(DynamicCard, { title: title, description: description, actions: actions, badges: badges, shadow: shadow, variant: variant, className: className }));
};
// Sample data for stories (updated with new badge variants)
export var sampleKPIData = [
    { title: 'Spend', value: '$7.89m', trend: { direction: 'down', value: '21.74%', showIcon: true } },
    { title: 'Clicks', value: '51.60K', trend: { direction: 'up', value: '27.89%', showIcon: true } },
    { title: 'New users', value: '2,345', trend: { direction: 'up', value: '15.32%', showIcon: true } },
    { title: 'Churn rate', value: '4.20%', trend: { direction: 'down', value: '1.45%', showIcon: true } },
];
